import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { api, extractApiError } from "../lib/api";

const INVALID_LINK_MESSAGE = "Dieser Link ist ungültig oder nicht mehr verfügbar.";

function getStatusLabel(status) {
  if (status === "granted") return "Erlaubt";
  if (status === "revoked") return "Widerrufen";
  return "Nicht erteilt";
}

function getConsentAction(status, consentType) {
  const isGranted = status?.status === "granted";
  if (consentType === "marketing_communication") {
    return isGranted
      ? {
          endpoint: "revoke",
          label: "Marketing-Kommunikation widerrufen",
          testId: "revoke-marketing-button",
        }
      : {
          endpoint: "grant",
          label: "Marketing-Kommunikation erteilen",
          testId: "grant-marketing-button",
        };
  }
  return isGranted
    ? {
        endpoint: "revoke",
        label: "E-Mail-Tracking widerrufen",
        testId: "revoke-tracking-button",
      }
    : {
        endpoint: "grant",
        label: "E-Mail-Tracking erteilen",
        testId: "grant-tracking-button",
      };
}

function ConsentStatusCard({ title, description, status, action, onAction, busy }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div>
        <div className="font-medium">{title}</div>
        <div className="text-sm text-muted-foreground">{description}</div>
      </div>
      <div className="text-sm">
        Status: <span className="font-medium">{getStatusLabel(status?.status)}</span>
      </div>
      <Button data-testid={action.testId} onClick={onAction} disabled={busy}>
        {busy ? "Wird aktualisiert ..." : action.label}
      </Button>
    </div>
  );
}

function PrivacyStatusCard({ status }) {
  return (
    <div
      className="rounded-xl border border-border bg-card p-4 space-y-3"
      data-testid="privacy-info-card"
    >
      <div>
        <div className="font-medium">Datenschutz / Anfrage</div>
        <div className="text-sm text-muted-foreground">
          Diese Bestätigung dient der Dokumentation Ihrer Anfrage und des zugehörigen
          Datenschutzhinweises.
        </div>
      </div>
      <div className="text-sm">
        Status: <span className="font-medium">{getStatusLabel(status?.status)}</span>
      </div>
      <div className="text-sm text-muted-foreground">
        Dieser Hinweis kann hier nicht separat widerrufen werden.
      </div>
    </div>
  );
}

export default function PublicConsentPage() {
  const { token = "" } = useParams();
  const [consentInfo, setConsentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [busyType, setBusyType] = useState("");

  const loadConsentInfo = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get(`/api/public/consents/${token}`);
      setConsentInfo(response.data);
    } catch (err) {
      setConsentInfo(null);
      setError(extractApiError(err, INVALID_LINK_MESSAGE));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadConsentInfo();
  }, [loadConsentInfo]);

  const handleUpdateConsent = useCallback(
    async (consentType, endpoint) => {
      setBusyType(consentType);
      setSuccessMessage("");
      try {
        const response = await api.post(`/api/public/consents/${token}/${endpoint}`, {
          consent_types: [consentType],
        });
        setConsentInfo(response.data);
        setSuccessMessage("Ihre Einwilligungen wurden aktualisiert.");
      } catch (err) {
        setError(extractApiError(err, INVALID_LINK_MESSAGE));
      } finally {
        setBusyType("");
      }
    },
    [token],
  );

  const lastChangedText = useMemo(() => {
    if (!consentInfo?.last_changed_at) return "";
    try {
      return new Intl.DateTimeFormat("de-DE", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(consentInfo.last_changed_at));
    } catch {
      return "";
    }
  }, [consentInfo]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 text-sm text-muted-foreground">
        Wird geladen ...
      </div>
    );
  }

  if (error || !consentInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-background">
        <div className="max-w-md w-full rounded-2xl border border-border bg-card p-6 text-center space-y-3">
          <h1 className="text-xl font-semibold">Einwilligungen verwalten</h1>
          <p className="text-sm text-muted-foreground">{error || INVALID_LINK_MESSAGE}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground px-4 py-10">
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="space-y-2">
          <div className="text-sm uppercase tracking-wide text-muted-foreground">
            {consentInfo.tenant_name || "FyreCRM"}
          </div>
          <h1 className="text-3xl font-semibold">Einwilligungen verwalten</h1>
          <p className="text-sm text-muted-foreground">
            Hier können Sie Einwilligung erteilen oder widerrufen und Marketing-Kommunikation
            sowie E-Mail-Tracking getrennt verwalten.
          </p>
          <p className="text-sm">E-Mail: {consentInfo.email}</p>
          {lastChangedText ? (
            <p className="text-sm text-muted-foreground">Letzte Änderung: {lastChangedText}</p>
          ) : null}
          {successMessage ? <p className="text-sm text-green-700">{successMessage}</p> : null}
        </div>

        <div className="grid gap-4">
          <ConsentStatusCard
            title="Marketing-Kommunikation"
            description="Produktinformationen, Fachinhalte und werbliche Follow-ups."
            status={consentInfo.marketing}
            action={getConsentAction(consentInfo.marketing, "marketing_communication")}
            onAction={() => {
              const action = getConsentAction(consentInfo.marketing, "marketing_communication");
              handleUpdateConsent("marketing_communication", action.endpoint);
            }}
            busy={busyType === "marketing_communication"}
          />
          <ConsentStatusCard
            title="E-Mail-Tracking"
            description="Öffnungs- und Klicktracking für zukünftige externe E-Mails."
            status={consentInfo.tracking}
            action={getConsentAction(consentInfo.tracking, "email_tracking")}
            onAction={() => {
              const action = getConsentAction(consentInfo.tracking, "email_tracking");
              handleUpdateConsent("email_tracking", action.endpoint);
            }}
            busy={busyType === "email_tracking"}
          />
          <PrivacyStatusCard status={consentInfo.privacy_request_processing} />
        </div>
      </div>
    </div>
  );
}
